/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { NoteService } from 'app/core/services/note-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import * as Core from 'app/core/models/essette-api';

@autoinject
export class NoteHistory extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ changeHandler: 'reload' })
    public note: Core.Note | null = null;

    protected noteHistory: ReadonlyArray<Core.NoteHistory> = [];

    protected sorting: Sorting = new Sorting();
    protected defaultSorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 10;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly noteService: NoteService,
        ...rest: any[]) {
        super(...rest);

        // Default sort
        this.defaultSorting.entries = [
            {
                reference: 'metadata.modified.timestamp',
                direction: SortDirection.Descending
            }
        ];
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadNoteHistory();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadNoteHistory(): Promise<void> {
        if(!this.note?.id) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting.entries.length ? this.sorting : this.defaultSorting
        };

        const includes = [
            'metadata.modified.byUser'
        ];

        const fields = [
            'metadata.modified.byUser.description',
            'metadata.modified.timestamp',
            'text'
        ];

        try {
            this.state.beginLoad();
            const response = await this.noteService.loadNoteHistory(this.note.id, options, includes, fields);
            this.noteHistory = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
